import React, { useEffect, useRef, useState } from 'react';
import DiamondVPNService from '../services/api';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/support.css';

const SupportChat = ({ isAuthenticated }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const lastIdRef = useRef(0);
  const seenIdsRef = useRef(new Set());
  const pollRef = useRef(null);
  const listRef = useRef(null);
  const { t } = useTranslation();

  const scrollToBottom = (smooth = false) => {
    requestAnimationFrame(() => {
      if (!listRef.current) return;
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    });
  };

  useEffect(() => {
    if (open) scrollToBottom();
  }, [open, messages]);

  useEffect(() => {
    if (!open) {
      stopPolling();
      return;
    }
    (async () => {
      try {
        await DiamondVPNService.getSupportThread();
        // reset state and refs
        setMessages([]);
        lastIdRef.current = 0;
        seenIdsRef.current = new Set();
        await fetchMessages(0);
        startPolling();
      } catch (e) {
        console.error('Support thread error', e);
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(() => fetchMessages(), 10000);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchMessages = async (after) => {
    try {
      const afterId = typeof after === 'number' ? after : lastIdRef.current;
      const data = await DiamondVPNService.getSupportMessages(afterId > 0 ? afterId : 0);
      if (Array.isArray(data.messages) && data.messages.length) {
        const newOnes = [];
        for (const m of data.messages) {
          if (!seenIdsRef.current.has(m.id)) {
            seenIdsRef.current.add(m.id);
            newOnes.push(m);
            if (m.id > lastIdRef.current) lastIdRef.current = m.id;
          }
        }
        if (newOnes.length) {
          setMessages(prev => [...prev, ...newOnes]);
        }
      }
    } catch (e) {
      console.error('Load messages failed', e);
    }
  };

  const sendMessage = async () => {
    if (sending) return;
    const text = (input || '').trim();
    if (!text && !photo) return;
    if (text.length > 4000) {
      alert(t('supportChat.too_long'));
      return;
    }
    try {
      setSending(true);
      const data = await DiamondVPNService.sendSupportMessage(text, photo);
      if (data && data.message) {
        if (!seenIdsRef.current.has(data.message.id)) {
          seenIdsRef.current.add(data.message.id);
          setMessages(prev => [...prev, data.message]);
          lastIdRef.current = Math.max(lastIdRef.current, data.message.id);
        }
        setInput('');
        clearPhoto();
      }
      // Try to fetch an auto-reply right away (and once more shortly after).
      await fetchMessages();
      setTimeout(() => { fetchMessages(); }, 1200);
    } catch (e) {
      console.error('Send message failed', e);
    } finally {
      setSending(false);
    }
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const setPhotoFile = (file) => {
    if (!file) return false;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
      alert('Можно прикрепить JPG, PNG или WEBP до 10 МБ.');
      return false;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    return true;
  };

  const onPhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!setPhotoFile(file)) {
      event.target.value = '';
    }
  };

  const onPaste = (event) => {
    if (sending) return;
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find(item => item.kind === 'file' && item.type.startsWith('image/'));
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    event.preventDefault();
    const ext = file.type === 'image/webp' ? 'webp' : file.type === 'image/jpeg' ? 'jpg' : 'png';
    const namedFile = file.name
      ? file
      : new File([file], `screenshot-${Date.now()}.${ext}`, { type: file.type });
    setPhotoFile(namedFile);
  };

  const imageUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${DiamondVPNService.baseURL}${url}`;
  };

  if (!isAuthenticated) return null;

  return (
    <div className="support-widget">
      {!open && (
        <button
          className="support-fab"
          onClick={() => setOpen(true)}
          aria-label={t('supportChat.open')}
          title={t('supportChat.open')}
        >
          {String.fromCodePoint(0x1F4AC)}
        </button>
      )}
      {open && (
        <div className="support-panel">
          <div className="support-header">
            <span>{t('supportChat.title')}</span>
            <button
              className="support-close"
              onClick={() => setOpen(false)}
              aria-label={t('supportChat.close')}
              title={t('supportChat.close')}
            >
              {String.fromCharCode(0x00D7)}
            </button>
          </div>
          <div className="support-messages" ref={listRef}>
            {messages.map(m => (
              <div key={m.id} className={`s-msg ${m.sender === 'user' ? 's-user' : 's-admin'}`}>
                {m.attachment_url && (
                  <a href={imageUrl(m.attachment_url)} target="_blank" rel="noreferrer">
                    <img
                      className="s-image"
                      src={imageUrl(m.attachment_url)}
                      alt={m.attachment_name || 'attachment'}
                      onLoad={() => scrollToBottom()}
                    />
                  </a>
                )}
                {m.text && <div className="s-text">{m.text}</div>}
                <div className="s-time">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="s-empty">{t('supportChat.empty')}</div>
            )}
          </div>
          {photoPreview && (
            <div className="support-photo-preview">
              <img src={photoPreview} alt="preview" />
              <span>{photo?.name}</span>
              {sending && <em>{t('supportChat.sending')}</em>}
              <button type="button" onClick={clearPhoto} disabled={sending} aria-label="Remove photo">×</button>
            </div>
          )}
          <div className={`support-input${sending ? ' is-sending' : ''}`}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onPhotoChange}
              className="support-file-input"
              disabled={sending}
            />
            <button
              type="button"
              className="support-attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              title="Attach photo"
              aria-label="Attach photo"
            >
              📎
            </button>
            <input
              type="text"
              value={input}
              maxLength={4000}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('supportChat.placeholder')}
              disabled={sending}
              onPaste={onPaste}
              onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
            />
            <button
              type="button"
              className="support-send"
              onClick={sendMessage}
              disabled={sending || (!(input || '').trim() && !photo)}
            >
              {sending ? t('supportChat.sending') : t('supportChat.send')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportChat;










